import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useUserProfile } from '../../hooks/useUserProfile';
import { helpContent, type HelpKey } from '../../config/helpContent';
import { Z_INDEX } from '../../styles/zIndex';
import styles from './HelpTooltip.module.css';

type CommonProps = {
  /** アクセシビリティ用のラベル。省略時は「ヘルプを表示」 */
  ariaLabel?: string;
  /** 表示位置。デフォルトは 'top'。画面端では自動で反転する */
  placement?: 'top' | 'bottom';
  /** トリガー側に付与する追加クラス */
  className?: string;
};

type KeyedProps = CommonProps & {
  /** 集中管理された文言キー（config/helpContent）。推奨。 */
  contentKey: HelpKey;
  children?: never;
};

type InlineProps = CommonProps & {
  contentKey?: never;
  /** ad-hoc な本文（OEM 分岐の必要がないワンオフ用途向け） */
  children: ReactNode;
};

type Props = KeyedProps | InlineProps;

/**
 * ヘルプ用ツールチップ。
 * - クリック/タップで開閉（ホバーでは開かない）
 * - マイページで `show_help_tooltips` を OFF にするとアイコン自体が非表示
 * - `contentKey` で [config/helpContent](../../config/helpContent) のブランド別文言を参照（推奨）
 * - 集中管理しない単発利用の場合は `children` に JSX を直接渡す
 */
export function HelpTooltip({ ariaLabel = 'ヘルプを表示', placement = 'top', className, ...rest }: Props) {
  const body: ReactNode = 'contentKey' in rest && rest.contentKey !== undefined
    ? helpContent[rest.contentKey]
    : (rest as InlineProps).children;
  const { data: profile } = useUserProfile();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [arrowOffset, setArrowOffset] = useState<number>(0);
  const [arrowPosition, setArrowPosition] = useState<'top' | 'bottom'>('top');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !bubbleRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const bubbleRect = bubbleRef.current.getBoundingClientRect();

    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    let left = triggerCenterX - bubbleRect.width / 2;
    let top: number;
    let arrow: 'top' | 'bottom';

    if (placement === 'bottom') {
      top = triggerRect.bottom + 10;
      arrow = 'bottom';
      if (top + bubbleRect.height > window.innerHeight) {
        top = triggerRect.top - bubbleRect.height - 10;
        arrow = 'top';
      }
    } else {
      top = triggerRect.top - bubbleRect.height - 10;
      arrow = 'top';
      if (top < 0) {
        top = triggerRect.bottom + 10;
        arrow = 'bottom';
      }
    }

    if (left < 8) left = 8;
    if (left + bubbleRect.width > window.innerWidth - 8) {
      left = window.innerWidth - bubbleRect.width - 8;
    }

    setPosition({ top, left });
    setArrowOffset(triggerCenterX - left);
    setArrowPosition(arrow);
  }, [placement]);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    const handleScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (triggerRef.current?.contains(target)) return;
      if (bubbleRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    window.addEventListener('keydown', handleKey);
    window.addEventListener('pointerdown', handlePointerDown);
    return () => {
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isOpen]);

  if (profile && profile.show_help_tooltips === false) {
    return null;
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        className={`${styles.trigger} ${isOpen ? styles.open : ''} ${className ?? ''}`}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
      >
        <img src="/help.svg" alt="" className={styles.icon} aria-hidden="true" />
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={bubbleRef}
            role="tooltip"
            className={`${styles.bubble} ${arrowPosition === 'top' ? styles.arrowBottom : styles.arrowTop}`}
            style={{
              position: 'fixed',
              top: `${position.top}px`,
              left: `${position.left}px`,
              zIndex: Z_INDEX.tooltip,
              ['--help-arrow-left' as string]: `${arrowOffset}px`,
            }}
          >
            {body}
            <a
              href="/profile?highlight=help-tooltips#help-tooltips-setting"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.settingsLink}
              aria-label="ヘルプの表示設定を変更"
              title="表示設定を変更"
              onClick={(e) => e.stopPropagation()}
            >
              <img src="/cog.svg" alt="" className={styles.settingsIcon} aria-hidden="true" />
            </a>
          </div>,
          document.body
        )}
    </>
  );
}
